import type { CompleteEvent, EditorEvent, TunaEditor } from '@tunasong/plugin-lib'
import { Editor, Transforms } from '@tunasong/plugin-lib'
import { Node } from 'slate'
import { ReactEditor } from 'slate-react'
import { useCurrentUser } from '@tunasong/redux'
import type { Comment, CoreElement } from '@tunasong/schemas'
import { ArrowUp, Block, Todo } from '@tunasong/icons'
import { Button, ToggleButton } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatEditor } from '../editor/chat-editor.js'
import { composerDraft, setComposerDraft } from './composer-drafts.js'
import { isComposerEmpty } from './composer-empty.js'
import styles from './composer.module.css'

/** What the composer is doing; an agent turn drives it through `AgentChat` (chat makeover #2718) */
export type ComposerState = 'idle' | 'sending' | 'streaming' | 'stopping'

/** The hint, short enough for the sidebar's row (GH #3161, strings approved by Brian) */
const singleLineHint = 'Enter to send'
const multiLineHint = 'Shift+Enter new line'
/** T1 and T2 (chat tasks, GH #3041) */
const taskLabel = 'Task'
const taskPlaceholder = 'Write a task…'
/** Send is a round icon button, named for what it sends (GH #3161) */
const sendCommentLabel = 'Send comment'
const sendTaskLabel = 'Send task'

/** How a comment is sent: as a task, when the composer's Task toggle was pressed */
export interface ComposerSendOptions {
  task: boolean
  /**
   * What the box held when it was sent. The comment is this, not the copy the box last reported through `onChange`:
   * a box whose text was never reported (a restored draft, a change not yet delivered) sent an empty comment (GH #3146).
   */
  body?: CoreElement[]
}

export interface ComposerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  onChange: (comment: Comment) => void
  /**
   * Send what was typed. Resolve `false` when the send failed: the box then puts the text back (GH #2453). `retry`
   * submits the same text again, the same way.
   */
  onComplete: (event: EditorEvent, retry: () => Promise<void>, options: ComposerSendOptions) => void | Promise<boolean>
  /** C2 for an agent ("Ask {agent}…"), C12 for people ("Write a comment…") */
  placeholder?: string
  /** The hint in the row under the field; "Enter to send", or "Shift+Enter new line" once the text is multi-line */
  hint?: string
  /** A status that replaces the hint, e.g. a Stop that failed */
  notice?: string
  /**
   * Offer the Task chip (T1) in the row under the field: people Chat does; the agents never do (Seymour, 2026-11-02).
   * Pressed, the placeholder reads T2, Send reads "Send task" and the next comment is sent as a task; it resets after
   * each send.
   */
  taskToggle?: boolean
  state?: ComposerState
  /** Shown while `state` is streaming, e.g. C8 "{agent} is answering…" */
  streamingLabel?: string
  /** With a Stop to offer, Send becomes Stop while streaming */
  onStop?: () => void
  /** The anchored ask (the agent focus chip), shown inside the composer */
  focusChip?: ReactNode
  /**
   * One-shot draft to insert. When `seed.seq` changes, the composer clears any
   * current content, inserts `seed.text`, and focuses the input. The parent
   * owns consumption bookkeeping (a new hand-off always carries a new seq), so
   * there is no consumed-callback.
   */
  seed?: { text: string; seq: number }
  /**
   * Where this box keeps what was typed but not sent, `${entityId}:${threadId ?? ''}` (chat tasks M5.4): it starts from
   * that draft, so a draft carries across the panel and full page. Without one, nothing is kept.
   */
  draftKey?: string
  /** The box takes focus as it mounts; Chat full page turns it off, as its h1 has focus (spec §10.8) */
  autoFocus?: boolean
  /**
   * Stable id forwarded to the composer's root element. Lets siblings
   * (like an empty-state header) wire `aria-describedby` to this composer so
   * screen readers hear the surrounding context.
   */
  id?: string
}

/**
 * The one composer for people Chat and the agents (chat makeover #2718). A Slate editor, so mentions, chords and
 * links still work in comments. Enter sends and Shift+Enter starts a new line (the `single-line` plugin); the field
 * grows from two lines to six.
 *
 * Stacked (GH #3161, option A, chosen by the user): the field spans the width, with no avatar since it is always the
 * current user, and one row under it holds the Task chip, the hint and a round Send. The Tab order is the DOM order:
 * field, Task, Send (#3151).
 */
export const Composer: FC<ComposerProps> = props => {
  const {
    onChange,
    onComplete,
    placeholder,
    hint,
    notice,
    taskToggle = false,
    state = 'idle',
    streamingLabel,
    onStop,
    focusChip,
    seed,
    draftKey,
    autoFocus = true,
    id,
    className,
    ...restProps
  } = props

  const { userId = 'unknown' } = useCurrentUser()
  const [isTask, setTask] = useState(false)
  const sendsTask = taskToggle && isTask
  /** More than one line typed: the hint turns to Shift+Enter */
  const [isMultiLine, setMultiLine] = useState(false)
  const chatElement = useMemo(
    () =>
      ({
        type: 'comment',
        userId,
        children: (draftKey ? composerDraft(draftKey) : undefined) ?? [{ type: 'line', children: [{ text: '' }] }],
      }) satisfies Partial<CoreElement<Comment>>,
    [userId, draftKey]
  )

  /** What is typed goes to the owner, and is kept as the draft; an emptied box keeps none */
  const handleChange = (comment: Comment) => {
    onChange(comment)
    setMultiLine(comment.children.length > 1)
    if (draftKey) {
      const typed = comment.children.map(node => Node.string(node as Node)).join('')
      setComposerDraft(draftKey, typed.trim() ? (comment.children as CoreElement[]) : undefined)
    }
  }

  // useState (not useRef) so the seed useEffect re-fires when the editor
  // becomes available. With useRef, a seed set BEFORE the editor mounted
  // (e.g., from the block-affordance dispatch path, which dispatches the
  // hand-off right after opening the sidebar tool) was missed: the effect ran
  // once with editor === null, returned early, and never re-ran because
  // [seed] didn't change again. Tracking the editor in state makes
  // editor-ready a dependency change that retriggers the insert.
  const [editor, setEditor] = useState<TunaEditor | null>(null)
  const handleEditor = useCallback((e: TunaEditor) => {
    setEditor(e)
  }, [])

  /**
   * When `seed.seq` changes (OR when the editor becomes available with a seed
   * already set), replace the editor body with `seed.text` and focus the
   * input. Effect deps include `editor` so the mount-after-seed case
   * retriggers. Guarded by `lastSeedSeq` so the same seq does not re-insert
   * on unrelated re-renders.
   */
  const lastSeedSeq = useRef(0)
  useEffect(() => {
    if (!editor || !seed || seed.text.length === 0 || lastSeedSeq.current === seed.seq) {
      return
    }
    lastSeedSeq.current = seed.seq
    Editor.withoutNormalizing(editor, () => {
      const at = {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      }
      Transforms.delete(editor, { at })
      Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
      Transforms.insertText(editor, seed.text)
    })
    try {
      ReactEditor.focus(editor as unknown as ReactEditor)
    } catch {
      // Focus can throw if the editor is unmounted mid-insert; silent recovery
      // is the right call — the user can still click to focus.
    }
  }, [editor, seed])

  const handleComplete = async (event: EditorEvent): Promise<void> => {
    const { editor } = event.detail as CompleteEvent
    const typed = editor.children

    /** Clear the editor */
    Editor.withoutNormalizing(editor, () => {
      const at = {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      }
      Transforms.delete(editor, { at })
      Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
    })

    setMultiLine(false)
    const sent = await onComplete(event, () => handleComplete(event), {
      task: sendsTask,
      body: typed as CoreElement[],
    })
    /** Sent: the toggle resets. Failed: the text comes back, still a task, so it stays as it was */
    if (sent !== false) {
      setTask(false)
      if (draftKey) {
        setComposerDraft(draftKey, undefined)
      }
    }

    /**
     * A failed send used to lose what was typed (GH #2453). Put it back, unless something new was typed meanwhile.
     */
    if (sent === false && isComposerEmpty(editor)) {
      Editor.withoutNormalizing(editor, () => {
        const cleared = editor.children.length
        Transforms.insertNodes(editor, typed, { at: [0] })
        for (let index = 0; index < cleared; index++) {
          Transforms.removeNodes(editor, { at: [typed.length] })
        }
      })
    }
  }

  /** The Send button sends exactly as Enter does */
  const handleSend = async () => {
    if (!editor || isComposerEmpty(editor)) {
      return
    }
    /**
     * A real event, as Enter sends: the handler cancels it first, and a hand-built object had no preventDefault
     * (GH #3035). The button has no block, so the detail carries the editor alone, as before.
     */
    const detail = { type: 'complete', editor } as CompleteEvent
    await handleComplete(new CustomEvent('complete', { detail, cancelable: true }))
  }

  const isAnswering = state === 'streaming' || state === 'stopping'
  const hintText =
    isAnswering && streamingLabel ? streamingLabel : (hint ?? (isMultiLine ? multiLineHint : singleLineHint))

  return (
    <div
      className={className ? `${styles.container} ${className}` : styles.container}
      id={id}
      data-testid="composer"
      {...restProps}
    >
      {focusChip ? <div className={styles.focus}>{focusChip}</div> : null}
      <ChatEditor
        /** Another draft (another thread) starts its own box */
        key={draftKey}
        className={styles.editor}
        element={chatElement}
        autoFocus={autoFocus}
        placeholder={sendsTask ? taskPlaceholder : placeholder}
        placeholderWhenFocused={true}
        onChange={handleChange}
        onComplete={handleComplete}
        onEditor={handleEditor}
      />
      <div className={styles.actions}>
        {taskToggle ? (
          <ToggleButton
            value="task"
            size="small"
            selected={isTask}
            className={styles.taskChip}
            onClick={() => setTask(previous => !previous)}
          >
            {isTask ? <Todo aria-hidden={true} /> : <Block aria-hidden={true} />}
            {taskLabel}
          </ToggleButton>
        ) : null}
        {notice ? (
          <span className={styles.hint} role="status">
            {notice}
          </span>
        ) : (
          <span className={styles.hint}>{hintText}</span>
        )}
        {isAnswering && onStop ? (
          <Button size="small" variant="outlined" onClick={onStop} disabled={state === 'stopping'}>
            Stop
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            className={styles.send}
            aria-label={sendsTask ? sendTaskLabel : sendCommentLabel}
            onClick={() => void handleSend()}
            disabled={state === 'sending'}
          >
            <ArrowUp aria-hidden={true} />
          </Button>
        )}
      </div>
    </div>
  )
}
